import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda, getNegocioId } from '../services/negocioActual';
import { obtenerClientes } from '../services/clientesService';
import { obtenerProductos } from '../services/productosService';
import {
  obtenerCampanaActiva, crearCampana, actualizarCampana, obtenerEnvios, marcarEnviado, desmarcarEnviado, obtenerVentasConProducto,
} from '../services/recompraService';
import {
  GRUPOS, PLANTILLAS, perfilesRecompra, filtroCatalogo, loQueCompro, fechaLarga, armarMensaje, mesActual, inicioDia,
} from '../utils/recompra';
import { linkCatalogo, linkWhatsAppA } from '../utils/compartir';
import { obtenerCategoria } from '../utils/categorias';
import { useCategorias } from '../context/CategoriasContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { mensajeAmigable } from '../utils/errores';
import CampoNumero from './CampoNumero';

const ROSA = 'var(--color-primario,#DB2777)';
const dinero = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fechaCorta = (iso) => new Date(iso).toLocaleDateString('es-HN', { day: 'numeric', month: 'short' });
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MAX_DIA = 20; // recomendado para que WhatsApp no marque el número como spam

const pill = (activo) => ({
  padding: '7px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  border: `1px solid ${activo ? ROSA : '#E5E7EB'}`, backgroundColor: activo ? ROSA : 'white', color: activo ? 'white' : '#6B7280',
});
const tile = { backgroundColor: 'white', borderRadius: '12px', padding: '14px', border: '1px solid #F1EEF8' };

// 🔁 Campaña de recompra: a las clientas que ya compraron y no han vuelto se
// les escribe por WhatsApp, una por una, con su nombre, su talla, el catálogo
// filtrado (etiqueta "recompra") y su % de descuento de clienta. Se mide quién volvió.
export default function CampanaRecompraView({ onVolver }) {
  const { negocio } = useAuth();
  const { categorias } = useCategorias();
  const { mostrarToast } = useToast();
  const [campana, setCampana] = useState(undefined); // undefined = cargando, null = no hay
  const [datos, setDatos] = useState(null); // { clientes, ventas, productos }
  const [envios, setEnvios] = useState([]);
  const [error, setError] = useState(null);
  const [grupo, setGrupo] = useState('medio');
  const [vista, setVista] = useState('pendientes'); // pendientes | enviados | volvieron | todos
  const [verMensaje, setVerMensaje] = useState(null);
  const [ajustes, setAjustes] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // Formulario (nueva campaña o ajustes)
  const mes = mesActual();
  const [form, setForm] = useState({ nombre: `Recompra ${MESES[new Date().getMonth()]}`, descuentoPct: '10', desde: mes.desde, hasta: mes.hasta, mensajes: { ...PLANTILLAS } });

  useEffect(() => {
    let activo = true;
    Promise.all([obtenerCampanaActiva(), obtenerClientes(), obtenerVentasConProducto(), obtenerProductos()])
      .then(async ([c, clientes, ventas, productos]) => {
        if (!activo) return;
        setDatos({ clientes, ventas, productos });
        setCampana(c);
        if (c) {
          setForm({ nombre: c.nombre, descuentoPct: String(c.descuento_pct), desde: c.desde, hasta: c.hasta, mensajes: { ...PLANTILLAS, ...(c.mensajes || {}) } });
          setEnvios(await obtenerEnvios(c.id));
        }
      })
      .catch((e) => activo && setError(mensajeAmigable(e)));
    return () => { activo = false; };
  }, []);

  const desdeCampana = campana ? campana.desde : form.desde;
  const perfiles = useMemo(() => (datos ? perfilesRecompra(datos.clientes, datos.ventas, inicioDia(desdeCampana)) : []), [datos, desdeCampana]);
  const envioDe = useMemo(() => new Map(envios.map((e) => [e.cliente_id, e])), [envios]);
  const prefijoDe = (nombreCat) => obtenerCategoria(categorias, nombreCat)?.prefijo || null;

  // Para la campaña: las que todavía no han vuelto (o volvieron después del mensaje, para medir).
  const objetivo = perfiles.filter((p) => !p.volvio || envioDe.has(p.cliente.id));
  const volvieron = perfiles.filter((p) => p.volvio && envioDe.has(p.cliente.id) && new Date(p.volvio.primera) >= new Date(envioDe.get(p.cliente.id).enviado_en));
  const yaVolvieronSolas = perfiles.filter((p) => p.volvio && !envioDe.has(p.cliente.id)).length;
  const ventasCampana = volvieron.reduce((s, p) => s + p.volvio.total, 0);
  const hoyStr = new Date().toDateString();
  const enviadosHoy = envios.filter((e) => new Date(e.enviado_en).toDateString() === hoyStr).length;

  const delGrupo = objetivo.filter((p) => p.grupo === grupo);
  const lista = delGrupo.filter((p) => {
    const env = envioDe.get(p.cliente.id);
    if (vista === 'pendientes') return !env;
    if (vista === 'enviados') return !!env;
    if (vista === 'volvieron') return volvieron.includes(p);
    return true;
  });

  const enlaceDe = (p) => {
    const f = filtroCatalogo(p, datos.productos, prefijoDe);
    return {
      url: linkCatalogo({ negocioId: getNegocioId(), slug: negocio?.slug, cat: f.cat, talla: f.talla, origen: 'recompra' }),
      talla: f.talla,
    };
  };

  const mensajeDe = (p) => {
    const e = enlaceDe(p);
    return armarMensaje(form.mensajes[p.grupo] || PLANTILLAS[p.grupo], {
      nombre: p.cliente.nombre, tienda: negocio?.nombre, loCompro: loQueCompro(p.ultimoProducto),
      talla: e.talla, descuento: form.descuentoPct, hasta: fechaLarga(form.hasta), enlace: e.url,
    });
  };

  const enviar = async (p) => {
    if (!p.cliente.telefono) { mostrarToast('Esta clienta no tiene teléfono guardado', 'error'); return; }
    window.open(linkWhatsAppA(p.cliente.telefono, mensajeDe(p)), '_blank', 'noopener,noreferrer');
    try {
      await marcarEnviado(campana.id, p.cliente.id, p.grupo);
      setEnvios((prev) => [...prev.filter((x) => x.cliente_id !== p.cliente.id), { cliente_id: p.cliente.id, grupo: p.grupo, enviado_en: new Date().toISOString() }]);
      if (enviadosHoy + 1 === MAX_DIA) mostrarToast(`Llevas ${MAX_DIA} mensajes hoy. Sigue mañana para que WhatsApp no marque tu número 🙏`, 'info', 7000);
    } catch (e) { mostrarToast(`Se abrió WhatsApp pero no se pudo marcar como enviado: ${mensajeAmigable(e)}`, 'error'); }
  };

  const desmarcar = async (p) => {
    try {
      await desmarcarEnviado(campana.id, p.cliente.id);
      setEnvios((prev) => prev.filter((x) => x.cliente_id !== p.cliente.id));
    } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); }
  };

  const guardarCampana = async () => {
    const pct = Number(form.descuentoPct);
    if (!(pct >= 0 && pct <= 90)) { mostrarToast('El descuento debe ser entre 0 y 90%', 'error'); return; }
    if (!form.hasta || form.hasta < form.desde) { mostrarToast('La fecha final debe ser después del inicio', 'error'); return; }
    setGuardando(true);
    try {
      if (campana) {
        await actualizarCampana(campana.id, { nombre: form.nombre, descuentoPct: pct, hasta: form.hasta, mensajes: form.mensajes });
        setCampana({ ...campana, nombre: form.nombre, descuento_pct: pct, hasta: form.hasta, mensajes: form.mensajes });
        mostrarToast('Campaña actualizada ✅', 'exito');
        setAjustes(false);
      } else {
        const c = await crearCampana({ ...form, descuentoPct: pct });
        setCampana(c);
        mostrarToast('🔁 Campaña creada. ¡A escribirles!', 'exito');
      }
    } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); } finally { setGuardando(false); }
  };

  const formulario = (
    <div className="k-card" style={{ padding: '16px', marginBottom: '14px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
        <label style={{ fontSize: '13px', fontWeight: 700 }}>Nombre
          <input className="k-input" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} style={{ marginTop: '4px', marginBottom: 0 }} />
        </label>
        <div style={{ fontSize: '13px', fontWeight: 700 }}>Descuento de clienta
          <CampoNumero valor={form.descuentoPct} onChange={(v) => setForm({ ...form, descuentoPct: v })} sufijo="%" decimales={0} min={0} max={90}
            maxMensaje="Máximo 90%" estilo={{ marginTop: '4px', width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box' }} />
        </div>
        <label style={{ fontSize: '13px', fontWeight: 700 }}>Válido hasta
          <input type="date" className="k-input" value={form.hasta} min={form.desde} onChange={(e) => setForm({ ...form, hasta: e.target.value })} style={{ marginTop: '4px', marginBottom: 0 }} />
        </label>
      </div>
      <details style={{ marginTop: '12px' }} open={!campana}>
        <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 700, color: ROSA }}>✏️ Mensajes de cada grupo</summary>
        <p style={{ margin: '6px 0', fontSize: '12px', color: '#6B7280' }}>
          Se rellenan solos: <code>{'{nombre}'}</code> <code>{'{tienda}'}</code> <code>{'{lo_que_compro}'}</code> <code>{'{talla_txt}'}</code> (" en su talla 36D" si hay piezas en su talla) <code>{'{descuento}'}</code> <code>{'{hasta}'}</code> <code>{'{enlace}'}</code>
        </p>
        {GRUPOS.map((g) => (
          <label key={g.clave} style={{ display: 'block', fontSize: '13px', fontWeight: 700, marginTop: '8px' }}>{g.icono} {g.etiqueta} <span style={{ fontWeight: 400, color: '#6B7280' }}>· {g.idea}</span>
            <textarea value={form.mensajes[g.clave]} rows={4} onChange={(e) => setForm({ ...form, mensajes: { ...form.mensajes, [g.clave]: e.target.value.slice(0, 900) } })}
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '4px', padding: '10px', borderRadius: '10px', border: '1px solid #D1D5DB', fontSize: '13px', fontFamily: 'inherit', fontWeight: 400 }} />
          </label>
        ))}
        <button type="button" onClick={() => setForm({ ...form, mensajes: { ...PLANTILLAS } })} className="k-btn-soft" style={{ marginTop: '6px', fontSize: '12px' }}>Volver a los mensajes sugeridos</button>
      </details>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
        {campana && <button onClick={() => setAjustes(false)} className="k-btn-soft">Cancelar</button>}
        <button onClick={guardarCampana} disabled={guardando}
          style={{ padding: '10px 18px', borderRadius: '10px', border: 'none', background: ROSA, color: 'white', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          {guardando ? 'Guardando…' : campana ? 'Guardar cambios' : '🔁 Empezar campaña'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="k-page" style={{ maxWidth: '960px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="k-page-title">🔁 Campaña de recompra</h2>
        <button onClick={onVolver} className="k-btn-soft">← Volver a Clientes</button>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6B7280' }}>
        Para las clientas que ya te compraron antes del {fechaLarga(desdeCampana)} y aún no vuelven. Les escribes una por una por WhatsApp con su nombre, su talla y el catálogo.
      </p>

      {error && <div style={{ padding: '14px', background: '#FEE2E2', borderRadius: '12px', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</div>}
      {campana === undefined && !error && <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>⏳ Revisando tus clientas…</div>}

      {campana === null && datos && (
        <>
          <div style={{ ...tile, marginBottom: '14px', background: '#FDF2F8', borderColor: '#FBCFE8' }}>
            <b>{perfiles.filter((p) => !p.volvio).length} clientas</b> te compraron antes y no han vuelto este mes:{' '}
            {GRUPOS.map((g) => `${g.icono} ${perfiles.filter((p) => !p.volvio && p.grupo === g.clave).length} ${g.rango}`).join(' · ')}
          </div>
          {formulario}
        </>
      )}

      {campana && datos && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '12px' }}>
            <div style={tile}><p style={{ margin: 0, fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>🎯 Clientas a recuperar</p><p style={{ margin: '2px 0 0', fontSize: '24px', fontWeight: 800 }}>{objetivo.length}</p></div>
            <div style={tile}><p style={{ margin: 0, fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>💬 Mensajes enviados</p><p style={{ margin: '2px 0 0', fontSize: '24px', fontWeight: 800 }}>{envios.length}</p><p style={{ margin: 0, fontSize: '12px', color: enviadosHoy >= MAX_DIA ? '#B45309' : '#9CA3AF' }}>Hoy: {enviadosHoy} de {MAX_DIA} recomendados</p></div>
            <div style={tile}><p style={{ margin: 0, fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>🛍️ Volvieron a comprar</p><p style={{ margin: '2px 0 0', fontSize: '24px', fontWeight: 800, color: '#047857' }}>{volvieron.length}</p><p style={{ margin: 0, fontSize: '12px', color: '#9CA3AF' }}>{envios.length ? `${Math.round((volvieron.length / envios.length) * 100)}% de las que recibieron mensaje` : 'después del mensaje'}</p></div>
            <div style={tile}><p style={{ margin: 0, fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>💰 Vendido por la campaña</p><p style={{ margin: '2px 0 0', fontSize: '24px', fontWeight: 800, color: ROSA }}>{dinero(ventasCampana)}</p>{yaVolvieronSolas > 0 && <p style={{ margin: 0, fontSize: '12px', color: '#9CA3AF' }}>+{yaVolvieronSolas} volvieron solas</p>}</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '13px' }}>
            <span style={{ background: '#FEF3C7', color: '#92400E', padding: '5px 10px', borderRadius: '999px', fontWeight: 700 }}>🎁 {campana.descuento_pct}% de clienta hasta el {fechaLarga(campana.hasta)}</span>
            <button onClick={() => setAjustes((a) => !a)} className="k-btn-soft" style={{ fontSize: '13px' }}>⚙️ {ajustes ? 'Cerrar ajustes' : 'Ajustar descuento, fecha o mensajes'}</button>
          </div>
          {ajustes && formulario}

          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {GRUPOS.map((g) => {
              const n = objetivo.filter((p) => p.grupo === g.clave);
              const faltan = n.filter((p) => !envioDe.has(p.cliente.id)).length;
              return (
                <button key={g.clave} onClick={() => setGrupo(g.clave)} style={{ ...pill(grupo === g.clave), textAlign: 'left', borderRadius: '12px' }}>
                  {g.icono} {g.etiqueta} ({n.length})<br /><small style={{ fontWeight: 400 }}>{g.idea} · faltan {faltan}</small>
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {[['pendientes', 'Por escribir'], ['enviados', 'Ya les escribí'], ['volvieron', '🛍️ Volvieron'], ['todos', 'Todas']].map(([k, t]) => (
              <button key={k} onClick={() => setVista(k)} style={{ ...pill(vista === k), padding: '5px 12px', fontSize: '12px' }}>{t}</button>
            ))}
          </div>

          {lista.length === 0 && <div style={{ textAlign: 'center', padding: '30px', color: '#9CA3AF' }}>{vista === 'pendientes' ? '🎉 Ya les escribiste a todas las de este grupo.' : 'Nadie en esta lista todavía.'}</div>}

          {lista.map((p) => {
            const env = envioDe.get(p.cliente.id);
            const volvio = volvieron.includes(p);
            const e = enlaceDe(p);
            return (
              <div key={p.cliente.id} className="k-card" style={{ padding: '12px 14px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ minWidth: 0 }}>
                    <b style={{ fontSize: '15px' }}>{p.cliente.nombre}</b>
                    <span style={{ fontSize: '12px', color: '#9CA3AF' }}> · 📱 {p.cliente.telefono || 'sin teléfono'}</span>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>
                      Última compra hace {p.dias} días ({fechaCorta(p.ultima)}) · {p.compras} compra(s) · {dinero(p.gastado)}
                      {p.categoria ? ` · ${p.categoria}` : ''}{p.talla ? ` · talla ${p.talla}` : ''}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                      🔗 {e.talla ? `Catálogo en su talla ${e.talla}` : 'Catálogo'}{env ? ` · ✅ Le escribiste el ${fechaCorta(env.enviado_en)}` : ''}
                      {volvio && <b style={{ color: '#047857' }}> · 🛍️ Volvió: {dinero(p.volvio.total)}</b>}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button onClick={() => setVerMensaje(verMensaje === p.cliente.id ? null : p.cliente.id)} className="k-btn-soft" style={{ fontSize: '12px' }}>👁 Mensaje</button>
                    <button onClick={() => enviar(p)} disabled={!p.cliente.telefono}
                      style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: '#25D366', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer', opacity: p.cliente.telefono ? 1 : 0.5, fontFamily: 'inherit' }}>
                      💬 {env ? 'Volver a escribir' : 'Escribirle'}
                    </button>
                    {env && <button onClick={() => desmarcar(p)} className="k-btn-soft" style={{ fontSize: '12px' }} title="Quitar la marca de enviado">↩️</button>}
                  </div>
                </div>
                {verMensaje === p.cliente.id && (
                  <pre style={{ margin: '10px 0 0', whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '13px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '10px', color: '#14532D' }}>{mensajeDe(p)}</pre>
                )}
              </div>
            );
          })}

          <p style={{ margin: '14px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
            💡 Escribe {MAX_DIA} o menos por día y personaliza si puedes: WhatsApp bloquea números que mandan muchos mensajes iguales. Cuando una clienta vuelva y le vendas, elígela como cliente en Vender: ahí te aparece el botón para aplicarle su {campana.descuento_pct}%.
            Los pedidos que lleguen por estos enlaces salen como "🔁 Recompra" en Pedidos → Estadísticas.
          </p>
        </>
      )}
    </div>
  );
}
